/** Canonical share / OG copy — keep in sync with `frontend/index.html` meta tags. */

export const SITE_OG_TITLE = "Cricket8 | Free Social Cricket Games";

export const SITE_OG_DESCRIPTION =
    "Play live cricket challenges with friends. Earn virtual Gold Balls and XP. Free to play — no cash prizes.";

export const OG_IMAGE_ALT = "Cricket8 - Don't just watch the match. Battle your friends.";

export const GAMES_PAGE_OG_TITLE = "All Games | Cricket8";

export const GAMES_PAGE_OG_DESCRIPTION =
    "Play free social cricket games on Cricket8 — Squares, Sixes, Bullion, Trader and Leagues. Compete with friends for Gold Balls and XP.";

export const ABOUT_PAGE_OG_TITLE = "About Cricket8 | Free Social Cricket Games";

export const ABOUT_PAGE_OG_DESCRIPTION =
    "Cricket8 is free-to-play social cricket gaming — no real money, no cash prizes. Play with friends, earn virtual Gold Balls and XP.";

export const HOW_TO_PLAY_PAGE_OG_TITLE = "How to Play | Cricket8";

export const HOW_TO_PLAY_PAGE_OG_DESCRIPTION =
    "Learn how to play Cricket8 — sign up, claim free Gold Balls, and jump into Squares, Sixes, Bullion, Trader and Leagues.";

export const CONTACT_PAGE_OG_TITLE = "Contact Us | Cricket8";

export const CONTACT_PAGE_OG_DESCRIPTION =
    "Get in touch with the Cricket8 team — questions, feedback and support for our free social cricket games.";

export const XP_LEADERBOARD_PAGE_OG_TITLE = "XP Leaderboard | Cricket8";

export const XP_LEADERBOARD_PAGE_OG_DESCRIPTION =
    "See who is climbing the Cricket8 XP leaderboard — compete with friends and the community for weekly crowns.";

export const MATCHES_PAGE_OG_TITLE = "Live & Upcoming Cricket Matches | Cricket8";

export const MATCHES_PAGE_OG_DESCRIPTION =
    "Browse live and upcoming cricket matches on Cricket8 — fixtures, scores, and quick links to play Squares, Sixes, Bullion and Trader.";

export const RESULTS_PAGE_OG_TITLE = "Cricket Results & Scores | Cricket8";

export const RESULTS_PAGE_OG_DESCRIPTION =
    "Explore cricket match results and scorecards on Cricket8 — final scores, innings summaries and match outcomes.";

export const SQUARES_PAGE_OG_TITLE = "Squares | Cricket8";

export const SQUARES_PAGE_OG_DESCRIPTION =
    "Play Squares on Cricket8 — live overs, compete with friends, and climb the match leaderboard.";

export const SQUARES_MATCH_PLAY_OG_TITLE = "Squares Match Play | Cricket8";

export const SIXES_PAGE_OG_TITLE = "Sixes | Cricket8";

export const SIXES_PAGE_OG_DESCRIPTION =
    "Play Sixes on Cricket8 — live match cricket cards, cheer for match events, and compete for points.";

export const BULLION_PAGE_OG_TITLE = "Bullion on Cricket8";

export const BULLION_PAGE_OG_DESCRIPTION =
    "Play Bullion on Cricket8 — public arenas and private battles. Pick your board and compete with friends.";

export const TRADER_PAGE_OG_TITLE = "Live sport games on Cricket8";

export const TRADER_PAGE_OG_DESCRIPTION =
    "Play live sport games on Cricket8 Trader — cricket, football, tennis and more. Free to play.";

export const LEAGUES_PAGE_OG_TITLE = "Leagues | Cricket8";

export const LEAGUES_PAGE_OG_DESCRIPTION =
    "Play Leagues on Cricket8 — private tables with friends, pick which games to play, and battle for the leaderboard.";

export const SQUARES_RULES_OG_TITLE = "Squares Rules | Cricket8";

export const SQUARES_RULES_OG_DESCRIPTION =
    "How to play Squares — live cricket ball-by-ball challenges on Cricket8.";

export const PROFILE_INVITE_SHARE_TITLE = "Play with friends on Cricket8";

export const PROFILE_INVITE_SHARE_TEXT = "Join me on Cricket8 — play live cricket games together.";

/** Keep aligned with `backend/controllers/seo/profile-share-preview.js` (PROFILE_OG_*). */
export const PROFILE_OG_TITLE = PROFILE_INVITE_SHARE_TITLE;

export const PROFILE_OG_DESCRIPTION =
    "Play live cricket games with friends on Cricket8 — test your cricket knowledge and compete together.";

export const PROFILE_OG_IMAGE_ALT = "Play with friends on Cricket8";

export function buildProfileInviteShareTitle(displayName?: string | null): string {
    const name = String(displayName ?? "").trim();
    return name ? `${name} invited you to play on Cricket8` : PROFILE_INVITE_SHARE_TITLE;
}

export function buildProfileInviteShareText(displayName?: string | null): string {
    const name = String(displayName ?? "").trim();
    return name
        ? `Join ${name} on Cricket8 — play live cricket games together.`
        : PROFILE_INVITE_SHARE_TEXT;
}

export function buildProfileOgDescription(displayName?: string | null): string {
    return buildProfileInviteShareText(displayName);
}

export const LOBBY_INVITE_MESSAGES = {
    bullion: [
        "Join me for Bullion on Cricket8 — public arenas and private battles: ",
        "I'm playing Bullion on Cricket8 — come pick your board for the same match: ",
        "Think you can beat my Bullion picks? Jump in here: ",
    ],
    squares: [
        "Join me for Squares on Cricket8 — pick runs on every ball of a live over: ",
        "Squares on Cricket8 — same over, same drama. Play here: ",
        "I'm playing Squares — take on all six balls here: ",
    ],
    trader: [
        "Join me on Cricket8 Trader — live sport games: ",
        "Play live sport games with me on Cricket8 — here's the lobby: ",
        "I'm on Cricket8 Trader — come compete: ",
    ],
    sixes: [
        "Join me for Sixes on Cricket8 — live match cricket cards: ",
        "I'm playing Sixes on Cricket8 — cheer for match events and climb the board: ",
        "Think you can beat my Sixes card? Play here: ",
    ],
    leagues: [
        "Join me on Cricket8 Leagues — private tables with friends, ranked on profit: ",
        "I'm playing Leagues on Cricket8 — come join a private table: ",
        "Come play a Cricket8 league with me — private table, ranked on profit: ",
        "Cricket8 Leagues — private tables for friends. Come play: ",
    ],
} as const;

/** Per-league join link (`/leagues?code=`) — not the Leagues lobby. Fallback when no fixture. */
export function leagueTableInviteFallbackMessage(listing: "private" | "public" = "private"): string {
    return listing === "public" ? "Join the Cricket8 League" : "Join my private Cricket8 League";
}

export const BULLION_PRIVATE_CONTEST_INVITE_MESSAGES = [
    "Join my private Bullion battle on Cricket8 — pick team, innings, player and match. ",
    "Think you can beat my Bullion lineup? Jump into my private battle: ",
    "I'm running a private Bullion pool for this match — join here: ",
    "Come play Bullion with me — private battle, same match: ",
    "Bullion private battle — entry link: ",
    "Compete with me in this private Bullion battle: ",
] as const;

export const BULLION_MATCH_LANDING_INVITE_MESSAGES = [
    "Join me for Bullion on Cricket8 — pick team, innings, player and match for this fixture: ",
    "I'm playing Bullion for this match — open the lobby and jump in: ",
    "Bullion arena lobby for this match — come play: ",
    "Think you can beat my picks? Bullion for this match is here: ",
    "Cricket8 Bullion — same match, public arenas and private battles: ",
] as const;

export const SQUARES_MATCH_PLAY_INVITE_MESSAGES = [
    "Join me in Squares — pick the runs for every ball and see who scores the most points! ",
    "Think you can beat my Squares picks? ",
    "Can you beat me in this match? ",
    "Can you beat my Squares score for this match? ",
    "Let's play Squares! Pick runs on every ball and see who scores the most points. ",
    "Join me in Squares! Pick runs on every ball and compete with friends. Let's play here: ",
] as const;
